<template>
    <section class="help-section">
        <div class="container mx-auto">
            <div class="link-wrapper">
                <NuxtLink :to="getL4InternalLink(blok.BackLink)" class="l4-links__link" external>
                    <NuxtImg
                        v-if="blok.BackLinkIcon?.filename"
                        :src="blok.BackLinkIcon.filename"
                        :title="blok.BackLinkIcon.title"
                        :alt="blok.BackLinkIcon.alt"
                        format="webp"
                        loading="lazy"
                    />
                    {{ blok.BackLinkText }}
                </NuxtLink>
            </div>
            <div class="content-container">
                <aside v-if="blok.content && !blok.hide_sidebar">
                    <h1 v-if="blok.title">{{ blok.title }}</h1>
                    <ul>
                        <template v-for="(content, index) in blok.content" :key="content._uid">
                            <li>
                                <a
                                    href="#"
                                    :class="{ active: activeTab === index }"
                                    @click.prevent="setActiveTab(index)"
                                >
                                    <NuxtImg
                                        v-if="content?.icon?.filename"
                                        :src="content.icon.filename"
                                        :title="content.icon.title"
                                        :alt="content.icon.alt"
                                        format="webp"
                                        loading="lazy"
                                        class="accordion-icon"
                                    />
                                    {{ content.title }}
                                </a>
                            </li>
                        </template>
                    </ul>
                </aside>
                <template v-for="(content, index) in blok.content" :key="content._uid">
                    <div v-show="index === activeTab" class="content">
                        <template v-for="item in content.content" :key="item._uid">
                            <Component :is="item.component" :blok="item"></Component>
                        </template>
                    </div>
                </template>
            </div>
            <hr class="divider" />
            <LiveChat />
        </div>
    </section>
</template>

<script setup lang="ts">
import type { IHelpSection, IFaq, IHelpSectionContent, IMultiFaqsSection } from '~/../types/components/L4HelpTabs';
import type { ISchemaFaqPage } from '~/../types/components/L4HelpTabs';

const { blok } = defineProps<{ blok: IHelpSection }>();
const activeTab = ref(0);
const route = useRoute();
const router = useRouter();

function clearQueryParams(): void {
    const newRoute = {
        ...route,
        query: {},
    };

    router.replace(newRoute);
}

const setActiveTab = (index: number) => {
    clearQueryParams();
    activeTab.value = index;
};

if (import.meta.server && blok.use_schema) {
    const schema: ISchemaFaqPage = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [],
    };

    blok.content.forEach((helpContentSection: IHelpSectionContent) => {
        helpContentSection.content?.forEach((content: IMultiFaqsSection) => {
            content.items?.forEach((item: IFaq) => {
                const entity = {
                    '@type': 'Question',
                    name: item.title,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: renderRichText(item.content),
                    },
                };
                schema.mainEntity.push(entity);
            });
        });
    });

    if (schema.mainEntity.length > 0) {
        // Set Schema List to <Head>
        useHead({
            script: [{ type: 'application/ld+json', innerHTML: schema }],
        });
    }
}

onMounted(() => {
    const section = route.query.section;

    if (section) {
        const index = Number(section) - 1;

        activeTab.value = index;
    }
});
</script>
<style scoped lang="postcss">
.container {
    @apply md:px-4;
}
.link-wrapper {
    a {
        @apply text-bodypurple text-[15px] leading-[17px] no-underline cursor-pointer inline-block relative md:pl-8 pl-9 md:my-5 my-3;
        &:hover {
            @apply underline;
        }
        img {
            @apply w-5 h-5 absolute ml-[-2rem] -translate-y-2/4 mr-0 my-0 top-2/4;
        }
    }
}
.divider {
    @apply hidden md:block;
}
aside {
    @apply border bg-[#f7f7f7] md:w-[30%] h-max pb-[15px] border-solid border-[#dfdfdf] mb-4 md:mb-0;

    h1 {
        @apply text-primary font-semibold text-[1.375rem] leading-[17px] mx-auto my-0 py-6 px-4 flex justify-center;
    }

    ul li a {
        @apply relative no-underline cursor-pointer text-lg font-semibold text-[#323232] block leading-[17px] pl-12 py-4;

        &.active {
            @apply bg-[#e8e6e6] font-bold;

            &:after {
                @apply md:content-[''] absolute -translate-y-2/4 w-0 h-0 border-l-[19px] border-r-0 border-y-[24px] border-solid border-[transparent_transparent_transparent_#e8e6e6] left-full top-2/4;
            }
        }

        .accordion-icon {
            content: '';
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            left: 0.75rem;
            width: 20px;
            height: 20px;
        }

        &:hover {
            @apply text-[#9970ab] bg-[#e8e6e6];
        }
    }
}

.content-container {
    @apply flex justify-between mb-4 md:flex-row flex-col;

    .content {
        @apply md:w-[68%] md:pl-6 px-4 md:px-0;

        :deep(.multi-faqs) {
            h3 {
                @apply font-semibold text-[2rem] leading-none text-primary mt-2 mb-8;
            }

            .accordion {
                .accordion-title {
                    @apply no-underline cursor-pointer relative w-full block transition-[background] duration-[0.3s] ease-[ease] leading-6 font-semibold text-xl text-black m-0 py-6 border-b border-[#dfdfdf];
                    h2 {
                        @apply w-[90%];
                    }
                    &:before,
                    &:after {
                        @apply bg-[#dfdfdf] rounded absolute h-1 transition-all duration-[0.2s] ease-linear rotate-45 content-[''] w-[22px] -mt-0.5 right-[13px] top-2/4;
                    }

                    &:after {
                        @apply -rotate-45 right-0;
                    }
                }

                &.active {
                    .accordion-title {
                        @apply border-b-[transparent];

                        &:before {
                            @apply -rotate-45 bg-primary;
                        }

                        &:after {
                            @apply rotate-45 bg-primary;
                        }
                    }
                }
            }
            .rich-text a u {
                &:hover {
                    @apply no-underline;
                }
            }
        }
    }
}
:deep(.rich-text a) {
    @apply underline;
    &:hover {
        @apply no-underline;
    }
}
</style>
